/* eslint-disable react/jsx-no-constructed-context-values */
import { useQuery } from "@tanstack/react-query";
import { getAudienceAnalyticsURL, getBrandAnalyticsURL, getMagnetAnalyticsURL } from "apis/brand";
import React, { MouseEventHandler, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { downloadOnClick } from "utils";
import { useLoaderContext } from "components/context/LoaderContext";
import { BrandContext } from "../../components/context/BrandContext";
import { useRouter } from "../../components/hooks";
import { get } from "../../utils/request";
import { BRAND_ROUTE, TOAST_CONFIG } from "../../constants";

export type AnalyticsDataProviderContextType = {
  brandId: string;
  magnetId: string;
  audienceAnalyticsData: AudienceAnalyticsData[];
  audienceAnalyticsDataLoadingError: unknown;
  audienceAnalyticsDataLoading: boolean;
  isAudienceAnalyticsDataLoadingError: boolean;
  reloadAudienceAnalyticsData: () => void;
  isAudienceAnalyticsDataRefetching: boolean;
  brandAnalyticsData: BrandAnalyticsData;
  brandAnalyticsDataLoadingError: unknown;
  isBrandAnalyticsDataLoading: boolean;
  isBrandAnalyticsDataLoadingError: boolean;
  reloadBrandAnalyticsData: () => void;
  isBrandAnalyticsDataRefetching: boolean;
  brandAnalyticsTopMementosData: TopMementosData[];
  magnetAnalyticsTopMementosData: TopMementosData[];
  brandAnalyticsTopMementosDataLoadingError: unknown;
  isBrandAnalyticsTopMementosDataLoading: boolean;
  isBrandAnalyticsTopMementosDataLoadingError: boolean;
  reloadBrandAnalyticsTopMementosData: () => void;
  isBrandAnalyticsTopMementosDataRefetching: boolean;
  magnetAnalyticsData: BrandAnalyticsData;
  magnetAnalyticsDataLoadingError: unknown;
  isMagnetAnalyticsDataLoading: boolean;
  isMagnetAnalyticsDataLoadingError: boolean;
  reloadMagnetAnalyticsData: () => void;
  isMagnetAnalyticsDataRefetching: boolean;
  brandMagnets: Magnet[];
  brandMagnetsLoadingError: unknown;
  isBrandMagnetsLoading: boolean;
  isBrandMagnetsLoadingError: boolean;
  reloadBrandMagnets: () => void;
  isBrandMagnetsRefetching: boolean;
  getTrendsData: (label: string) => { labels: string[]; data: number[] };
  handleExportAudienceData: MouseEventHandler;
};

export const AnalyticsDataProviderContext = React.createContext<AnalyticsDataProviderContextType>({
  brandId: "",
  magnetId: "",
  audienceAnalyticsData: [],
  audienceAnalyticsDataLoadingError: false,
  audienceAnalyticsDataLoading: false,
  isAudienceAnalyticsDataLoadingError: false,
  reloadAudienceAnalyticsData: () => {},
  isAudienceAnalyticsDataRefetching: false,
  brandAnalyticsData: {} as BrandAnalyticsData,
  brandAnalyticsDataLoadingError: false,
  isBrandAnalyticsDataLoading: false,
  isBrandAnalyticsDataLoadingError: false,
  reloadBrandAnalyticsData: () => {},
  isBrandAnalyticsDataRefetching: false,
  magnetAnalyticsTopMementosData: {} as TopMementosData[],
  brandAnalyticsTopMementosData: {} as TopMementosData[],
  brandAnalyticsTopMementosDataLoadingError: false,
  isBrandAnalyticsTopMementosDataLoading: false,
  isBrandAnalyticsTopMementosDataLoadingError: false,
  reloadBrandAnalyticsTopMementosData: () => {},
  isBrandAnalyticsTopMementosDataRefetching: false,
  brandMagnets: [],
  brandMagnetsLoadingError: false,
  isBrandMagnetsLoading: false,
  isBrandMagnetsLoadingError: false,
  reloadBrandMagnets: () => {},
  isBrandMagnetsRefetching: false,
  magnetAnalyticsData: {} as BrandAnalyticsData,
  magnetAnalyticsDataLoadingError: false,
  isMagnetAnalyticsDataLoading: false,
  isMagnetAnalyticsDataLoadingError: false,
  reloadMagnetAnalyticsData: () => {},
  isMagnetAnalyticsDataRefetching: false,
  getTrendsData: (label) => ({ labels: [label], data: [0] }),
  handleExportAudienceData: () => {},
});

export default function AnalyticsdDataProvider({ children }: any) {
  const { setLoading } = useLoaderContext();
  const { brandId } = useContext(BrandContext);
  const router = useRouter();
  const { m: magnetId } = router.query;

  const handleDataFailure = () => {
    throw new Error("Something went wrong while fetching analytics data.");
  };

  const getAudienceAnalyticsData = () =>
    get(getAudienceAnalyticsURL(brandId as string), {
      processError: handleDataFailure,
    });

  const {
    isLoading: audienceAnalyticsDataLoading,
    isError: isAudienceAnalyticsDataLoadingError,
    data: audienceAnalyticsData,
    error: audienceAnalyticsDataLoadingError,
    refetch: refetchAudienceAnalyticsData,
    isRefetching: isAudienceAnalyticsDataRefetching,
  } = useQuery({
    queryKey: ["audience", brandId],
    queryFn: getAudienceAnalyticsData,
  });

  const getBrandAnalyticsTopMementosData = () =>
    get(`${getBrandAnalyticsURL(brandId as string)}/mementos`, {
      processError: handleDataFailure,
    });

  const {
    isLoading: isBrandAnalyticsTopMementosDataLoading,
    isError: isBrandAnalyticsTopMementosDataLoadingError,
    data: brandAnalyticsTopMementosDataResponse,
    error: brandAnalyticsTopMementosDataLoadingError,
    refetch: refetchBrandAnalyticsTopMementosData,
    isRefetching: isBrandAnalyticsTopMementosDataRefetching,
  } = useQuery({
    queryKey: ["brand-top-mementos", brandId],
    queryFn: getBrandAnalyticsTopMementosData,
    enabled: !!brandId,
  });

  const getMagnetAnalyticsTopMementosData = () =>
    get(`${getBrandAnalyticsURL(brandId as string)}/mementos?magnet_id=${magnetId}`, {
      processError: handleDataFailure,
    });

  const { data: magnetAnalyticsTopMementosDataResponse } = useQuery({
    queryKey: ["magnet-top-mementos", magnetId],
    queryFn: getMagnetAnalyticsTopMementosData,
    enabled: !!magnetId,
  });

  const getBrandAnalyticsData = () =>
    get(getBrandAnalyticsURL(brandId as string), {
      processError: handleDataFailure,
    });

  const {
    isLoading: isBrandAnalyticsDataLoading,
    isError: isBrandAnalyticsDataLoadingError,
    data: brandAnalyticsDataResponse,
    error: brandAnalyticsDataLoadingError,
    refetch: refetchBrandAnalyticsData,
    isRefetching: isBrandAnalyticsDataRefetching,
  } = useQuery({
    queryKey: ["brand", brandId],
    queryFn: getBrandAnalyticsData,
  });

  const getmagnetsForBrand = () =>
    get(`${BRAND_ROUTE}${brandId}/?type=dashboard`, {
      processError: handleDataFailure,
    });

  const {
    isLoading: isBrandMagnetsLoading,
    isError: isBrandMagnetsLoadingError,
    data: brandMagnets,
    error: brandMagnetsLoadingError,
    refetch: refetchBrandMagnets,
    isRefetching: isBrandMagnetsRefetching,
  } = useQuery({
    queryKey: ["magnets"],
    enabled: !!brandId,
    queryFn: getmagnetsForBrand,
  });

  const getMagnetAnalyticsData = () =>
    get(getMagnetAnalyticsURL(brandId as string, magnetId as string), {
      processError: handleDataFailure,
    });

  const {
    isLoading: isMagnetAnalyticsDataLoading,
    isError: isMagnetAnalyticsDataLoadingError,
    data: magnetAnalyticsDataResponse,
    error: magnetAnalyticsDataLoadingError,
    refetch: refetchMagnetAnalyticsData,
    isRefetching: isMagnetAnalyticsDataRefetching,
  } = useQuery({
    queryKey: ["magnet", magnetId],
    enabled: !!magnetId,
    queryFn: getMagnetAnalyticsData,
  });

  const reloadAudienceAnalyticsData = () => refetchAudienceAnalyticsData();
  const reloadBrandAnalyticsData = () => refetchBrandAnalyticsData();
  const reloadBrandAnalyticsTopMementosData = () => refetchBrandAnalyticsTopMementosData();
  const reloadMagnetAnalyticsData = () => refetchMagnetAnalyticsData();
  const reloadBrandMagnets = () => refetchBrandMagnets();

  useEffect(() => {
    if (
      isBrandAnalyticsDataLoadingError ||
      isAudienceAnalyticsDataLoadingError ||
      isMagnetAnalyticsDataLoadingError ||
      isBrandMagnetsLoadingError
    )
      toast.error("Failed to fetch Analytics details", TOAST_CONFIG);
  }, [
    isBrandAnalyticsDataLoadingError,
    isAudienceAnalyticsDataLoadingError,
    isMagnetAnalyticsDataLoadingError,
    isBrandMagnetsLoadingError,
  ]);

  useEffect(() => {
    console.log(brandAnalyticsTopMementosDataResponse);
  }, [brandAnalyticsTopMementosDataResponse]);

  const getTrendsData = (label: string) => {
    const { graph_data: graphData } = brandAnalyticsDataResponse.brand_data;
    const {
      labels,
      magnet_metrics: magnetGraphData,
      memento_metrics: mementoGraphData,
      user_metrics: userGraphData,
    } = graphData;
    const graphAllData = [...magnetGraphData, ...mementoGraphData, ...userGraphData];
    const { data } = graphAllData.filter((ele) => ele.label === label)[0];

    return { labels, data };
  };

  const handleExportAudienceData = async () => {
    if (brandId) {
      try {
        setLoading(true);
        const resp: Blob = await get(`${getAudienceAnalyticsURL(brandId)}/export`, {
          requestConfig: {
            responseType: "blob",
          },
          headers: {
            "Content-Type": "application/octate-stream",
          },
        });
        downloadOnClick(URL.createObjectURL(resp));
      } finally {
        setLoading(false);
      }
    }
  };

  const context = {
    brandId: brandId || "",
    magnetId,
    isAudienceAnalyticsDataLoadingError,
    audienceAnalyticsData,
    audienceAnalyticsDataLoadingError,
    audienceAnalyticsDataLoading,
    reloadAudienceAnalyticsData,
    isAudienceAnalyticsDataRefetching,
    brandAnalyticsData: brandAnalyticsDataResponse?.brand_data,
    brandAnalyticsDataLoadingError,
    isBrandAnalyticsDataLoading,
    isBrandAnalyticsDataLoadingError,
    reloadBrandAnalyticsData,
    isBrandAnalyticsDataRefetching,
    magnetAnalyticsTopMementosData: magnetAnalyticsTopMementosDataResponse?.mementos_data,
    brandAnalyticsTopMementosData: brandAnalyticsTopMementosDataResponse?.mementos_data,
    brandAnalyticsTopMementosDataLoadingError,
    isBrandAnalyticsTopMementosDataLoading,
    isBrandAnalyticsTopMementosDataLoadingError,
    reloadBrandAnalyticsTopMementosData,
    isBrandAnalyticsTopMementosDataRefetching,
    isBrandMagnetsLoading,
    isBrandMagnetsLoadingError,
    brandMagnets: brandMagnets?.magnets,
    brandMagnetsLoadingError,
    reloadBrandMagnets,
    isBrandMagnetsRefetching,
    magnetAnalyticsData: magnetAnalyticsDataResponse?.brand_data,
    magnetAnalyticsDataLoadingError,
    isMagnetAnalyticsDataLoading,
    isMagnetAnalyticsDataLoadingError,
    reloadMagnetAnalyticsData,
    isMagnetAnalyticsDataRefetching,
    getTrendsData,
    handleExportAudienceData,
  };
  return <AnalyticsDataProviderContext.Provider value={context}>{children}</AnalyticsDataProviderContext.Provider>;
}

export const useAnalyticsDataProviderContext = () => {
  const context = useContext(AnalyticsDataProviderContext);
  return context;
};
